function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// @ts-strict-ignore
import produce from 'immer';
import isEmpty from 'lodash/isEmpty';
import React, { useState } from 'react';
import { BehaviorMetric, BehaviorMetricRequest, Metric } from 'app_api/projects/metrics/types';
import { SkeletonListReact } from 'common/components/skeleton-list';
import { SkeletonListRowSize } from 'common/components/skeleton-list/types';
import { useMetrics } from 'common/contexts/react/metrics-resource-context';
import { EntryRowProps } from 'common/query-builder/query-entry-section/row-types/types';
import { Action, Entry, EntrySectionUpdates, EntryType, EntryWithDisplay, MetricEntry, QueryMetricEntry, QuerySavedMetricEntry, SelectorTypeDefinitions } from 'common/query-builder/query-entry-section/types';
import { ModelStatus } from 'common/util/model-resource/model-resource';
// eslint-disable-next-line import/no-cycle
import { ButtonReact } from 'common/components/button';
import { ButtonSize, ButtonTheme } from 'common/components/button/constants';
import { DisplaySize } from 'common/components/tooltip/types';
import { useBehaviors } from 'common/contexts/react/behaviors-context';
import { useCurrentProject } from 'common/contexts/react/current-project-context';
import { useTranslation } from 'common/i18n/hooks';
import { MetricUnitsButton } from 'common/query-builder/chart-customization/metric-units';
import { CallingLocation } from 'common/query-builder/chart-customization/tracking';
import { MenuType, MultiMetricsSectionItem } from 'common/query-builder/query-block/multi-metrics-menu/types';
import { QueryEntrySectionReact } from 'common/query-builder/query-entry-section';
import { EditControls } from 'common/query-builder/query-entry-section/components/edit-controls';
import { RowSelector } from 'common/query-builder/query-entry-section/components/selector';
import { ControlBarAction, ControlBarActionItems, ControlBarActionValue } from 'common/query-builder/query-entry-section/constants';
import { BaseEntryRow } from 'common/query-builder/query-entry-section/row-types/base';
import { getReferencedMetricsFromEntries } from 'common/query-builder/query-entry-section/row-types/formula-entry/utils';
import { setEntryVisibility, toggleExpandedRow } from 'common/query-builder/query-entry-section/row-types/row-util';
import { getEntries } from 'common/report/insights/util/events';
import type { BehaviorMetricDisplay, BehaviorShowClause, MetricMeasurement } from 'common/report/insights/util/types';
import { BehaviorsResource } from 'common/report/model-resources/behaviors';
import { getEditCapability } from 'common/report/util/saved-entities';
import { MetricType, TopLevelMetricType } from 'common/types/reports/bookmark';
import { saveMetric } from '../behavior-utils';
import { getHydratedShowClause, getMetricModel, isModifiedQuerySavedMetricEntry, MetricEntryUpdate } from '../metric-entry-util';

// TODO: Need to fix typing of ControlBarActionItemsType
const controlBarActionItems = ControlBarActionItems as any;

/**
 * In cases where there are local changes defer to those otherwise default
 * to the saved metric definition.
 */
const getFinalMetricWithLocalChanges = (metric: BehaviorMetric, clause: BehaviorShowClause, localDisplay: BehaviorMetricDisplay | undefined, behaviorsResource: BehaviorsResource): BehaviorShowClause => {
  const finalClause: BehaviorShowClause = {
    behavior: !isEmpty(clause.behavior) ? clause.behavior : metric.definition.behavior,
    measurement: !isEmpty(clause.measurement) ? clause.measurement : metric.definition.measurement,
    display: _objectSpread(_objectSpread({}, !isEmpty(clause.display) ? clause.display : metric.definition.display), localDisplay),
    isExpanded: true,
    name: clause.name || metric.name
  };
  // Hydrate saved behaviors
  // TODO: Should we just do this in `getEntries`?
  return getHydratedShowClause(finalClause, behaviorsResource);
};
const getUpdatedSavedMetricEntry = (entry: QuerySavedMetricEntry, modifiedParams: MetricEntryUpdate): QuerySavedMetricEntry => {
  return produce(entry, draft => {
    const updatedEvent: BehaviorShowClause = _objectSpread(_objectSpread({}, entry.event), {}, {
      type: TopLevelMetricType.Metric
    });
    if (modifiedParams.behavior) {
      updatedEvent.behavior = _objectSpread(_objectSpread({}, updatedEvent.behavior), modifiedParams.behavior);
    }
    if (modifiedParams.measurement) {
      updatedEvent.measurement = _objectSpread(_objectSpread({}, updatedEvent.measurement), modifiedParams.measurement as MetricMeasurement);
    }
    draft.isExpanded = true;
    draft.isHidden = entry.isHidden;
    draft.event = updatedEvent;
    if (modifiedParams.display) {
      draft.display = _objectSpread(_objectSpread({}, draft.display), modifiedParams.display);
    }
    return draft;
  });
};
export function SavedMetricEntryRow(props: EntryRowProps<QuerySavedMetricEntry>) {
  const behaviors = useBehaviors();
  const currentProject = useCurrentProject();
  const metrics = useMetrics();
  const i18n = useTranslation();
  const [entryEl, setEntryEl] = useState<HTMLDivElement | null>(null);
  const {
    t
  } = i18n;
  const model = getMetricModel(props.entry.event, metrics);
  const metric = model === null || model === void 0 ? void 0 : model.value;
  // Make sure we merge in local state with saved db state.
  const innerMetricShowClause: BehaviorShowClause | null = metric ?
  // @ts-ignore
  getFinalMetricWithLocalChanges(metric, props.entry.event, props.entry.display, behaviors) : null;
  const skeleton = <div className='mp-query-entry-row for-formula-definition'>
      <SkeletonListReact numRows={4} rowSize={SkeletonListRowSize.Large} className='mp-formulas-menu-skeleton-list' />
    </div>;
  if (!metric || !innerMetricShowClause) {
    return skeleton;
  }
  const innerMetricEntry = getEntries({
    showClauses: [innerMetricShowClause]
  })[0];
  const isLoading = model ? model.status === ModelStatus.Loading : true;
  const {
    isExpanded
  } = props.entry;

  // TODO state
  const isSaving = false;
  const canEdit = getEditCapability(metric).enableEdit;
  // @ts-ignore
  const isModified = metric && innerMetricShowClause ? isModifiedQuerySavedMetricEntry(props.entry, metric) : false;
  const canSave = isModified;
  if (isLoading || !innerMetricShowClause) {
    return skeleton;
  }
  const querySectionEntries = [];
  if (innerMetricEntry) {
    querySectionEntries.push(innerMetricEntry);
  }
  const onReset = (newMetric?: Metric) => {
    const updatedMetric = newMetric !== null && newMetric !== void 0 ? newMetric : metric;
    const updatedEntry: QuerySavedMetricEntry = {
      type: MetricType.SavedMetric,
      name: updatedMetric.name,
      event: {
        id: updatedMetric.id,
        type: TopLevelMetricType.Metric,
        behavior: {},
        measurement: {} as MetricMeasurement
      },
      isExpanded: true
    };
    props.updateEntry(Action.Edit, updatedEntry, {
      requery: true
    });
  };
  const handleSaveModifications = async function handleSaveModifications() {
    let saveAsNew = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    if (!innerMetricShowClause) {
      return;
    }
    const metricEntity: BehaviorMetricRequest = {
      definition: {
        behavior: innerMetricShowClause.behavior,
        measurement: innerMetricShowClause.measurement,
        display: props.entry.display
      },
      name: props.entry.event.name || metric.name,
      type: TopLevelMetricType.Metric
    };
    const {
      results
    } = await saveMetric(_objectSpread({
      metricEntity,
      metrics,
      i18n,
      currentProject
    }, saveAsNew ? {} : {
      metricEntityId: metric.id
    }));
    onReset(results ? Object.values(results)[0] : undefined);
  };
  const updateMetricDisplay = (entry: EntryWithDisplay) => {
    const updatedEntry = getUpdatedSavedMetricEntry(props.entry, {
      display: entry.display
    });
    props.updateEntry(Action.Edit, updatedEntry, {
      requery: true
    });
  };
  const onInnerMetricsUpdate = (updates: EntrySectionUpdates<Entry>) => {
    let updatedEntry: QuerySavedMetricEntry | null = null;
    // Ignore updates that collapse/expand inner metric.
    // TODO: Can we just disable this in the UI so it never happens?
    if (!updates.entries.length) {
      return;
    }
    const convertedMetrics = getReferencedMetricsFromEntries(updates.entries as Array<QueryMetricEntry | MetricEntry>);
    if (!convertedMetrics.length) {
      return;
    }
    updatedEntry = getUpdatedSavedMetricEntry(props.entry, convertedMetrics[0]);
    if (!updatedEntry) {
      return;
    }
    // The contents of a saved metric are not collapsible.
    updatedEntry.isExpanded = true;
    props.updateEntry(Action.Edit, updatedEntry, {
      requery: true
    });
  };
  const actionsRenderer = () => {
    // Always show a modified metric so user can see underlying changes.
    if (isModified && isExpanded) {
      return null;
    }
    return <ButtonReact className='expand-control' icon={isExpanded ? "collapse-diagonal" : "expand"} theme={ButtonTheme.Tertiary} size={ButtonSize.Medium} tooltip={{
      text: isExpanded ? t("query-builder.actions.collapse") : t("query-builder.actions.expand")
    }} onClick={() => toggleExpandedRow(props)} data-sentry-element="ButtonReact" data-sentry-component="actionsRenderer" data-sentry-source-file="index.tsx" />;
  };

  /**
   * Turns this entry into an empty inline entry.
   */
  const handleCreateInline = (menuType: MenuType) => {
    props.onEntryRowCreate({
      type: SelectorTypeDefinitions.Metrics,
      event: null,
      filters: []
    }, menuType);
  };
  const handleSelectMultiMetrics = (sectionItem: MultiMetricsSectionItem) => {
    props.onEntryRowSelect({
      type: SelectorTypeDefinitions.Metrics,
      event: null,
      filters: []
    }, props.entry, sectionItem, props.index);
  };
  const handleControlSelect = (value: ControlBarActionValue) => {
    switch (value.action) {
      case ControlBarAction.SaveMetric:
        {
          handleSaveModifications(false);
          break;
        }
      case ControlBarAction.Show:
      case ControlBarAction.Hide:
        setEntryVisibility(props, value.action === ControlBarAction.Show);
        break;
      default:
        props.onControlSelect(value);
    }
  };
  const selectorRenderer = () => {
    var _props$entry$event, _props$entry;
    return <RowSelector {...props} contextPanelParentElement={entryEl} isPlaceholder={false} selected={{
      menuType: MenuType.SavedMetric,
      value: _objectSpread(_objectSpread({}, metric), {}, {
        name: ((_props$entry$event = props.entry.event) === null || _props$entry$event === void 0 ? void 0 : _props$entry$event.name) || ((_props$entry = props.entry) === null || _props$entry === void 0 ? void 0 : _props$entry.name)
      })
    }} selectorType={SelectorTypeDefinitions.Metrics} onSelect={(sectionItem: MultiMetricsSectionItem) => handleSelectMultiMetrics(sectionItem)} onCreate={(ev: {
      detail: {
        item: MenuType;
      };
    }) => handleCreateInline(ev.detail.item)} data-sentry-element="RowSelector" data-sentry-component="selectorRenderer" data-sentry-source-file="index.tsx" />;
  };
  const renderEditControls = () => {
    const metricUnitsButton = <MetricUnitsButton callingLocation={CallingLocation.Metric} chartType={props.currentChartType} setEntry={entry => updateMetricDisplay(entry)} showDefaultMetricUnitSelector={props.showDefaultMetricUnitSelector} insideFooter entry={props.entry} />;
    return <>
        <EditControls resetTooltip={t("query-builder.metrics.reset-tooltip")} isSaving={isSaving} isSaved canSave={canSave} canEdit={canEdit} metricUnitsSelector={metricUnitsButton} canReset={isModified} onReset={onReset} entityType={t("query-builder.metrics.metric")} onSave={() => handleSaveModifications(false)} onClone={() => handleSaveModifications(true)} data-sentry-element="EditControls" data-sentry-source-file="index.tsx" />
      </>;
  };
  const queryEntrySection = <QueryEntrySectionReact elref='subEntry' className='sub-entry' {...props.nestedSectionParams} showDefaultMetricSelector={false} disableAddEntry disableCollapse addEntryMenuWidth={130} enableEventIcon entryType={EntryType.Event} showTopEvents={false} dateRange={props.sectionProps.dateRange} dateUnit={props.sectionProps.dateUnit} isReferencedMetrics metricsMode excludedMetricsRowTypes={[MetricType.Formula, MetricType.SavedMetric]} rowIndex={props.index} rowDraggable trackProperties={_objectSpread(_objectSpread({}, props.nestedSectionParams.trackProperties || {}), {}, {
    sectionName: "referenced-metrics"
  })} entries={querySectionEntries} onUpdate={onInnerMetricsUpdate} />;
  const suffixRenderer = () => {
    if (!isExpanded) {
      return null;
    }
    return <div className='mp-query-entry-row for-saved-metric-definition' data-sentry-component="suffixRenderer" data-sentry-source-file="index.tsx">
        {queryEntrySection}
        {renderEditControls()}
      </div>;
  };
  return <>
      <BaseEntryRow {...props} ref={setEntryEl} isPlaceholder={false} onControlSelect={v => handleControlSelect(v)} overflowControls={[_objectSpread(_objectSpread({}, controlBarActionItems[ControlBarAction.SaveMetric]), !canSave && {
      tooltip: {
        text: t("query-builder.metrics.no-unsaved-changes"),
        size: DisplaySize.Medium
      },
      isDisabled: true
    }), controlBarActionItems[props.entry.isHidden ? ControlBarAction.Show : ControlBarAction.Hide], controlBarActionItems[ControlBarAction.Duplicate], controlBarActionItems[ControlBarAction.Delete]]} renderActions={actionsRenderer} renderSelector={selectorRenderer} renderSuffix={suffixRenderer} showDefaultMetricUnitSelector={false} data-sentry-element="BaseEntryRow" data-sentry-source-file="index.tsx" />
    </>;
}